/* global MARKETING_HEADER_AND_FOOTER_VERSION */
import { mapObject, obtainLanguage, clone, arrayWithoutDublicates, isInt } from './utils';

import {
  domains,
  languages,
  supportedLanguages,
  fallbackLanguage,
  defaultScreenBreakpoint,
  a11yScreenBreakpoint,
  defaultTabIndex,
  notExposedLanguages,
} from './data/constants.js';

export const baseUrl = () => {
  const version =
    typeof MARKETING_HEADER_AND_FOOTER_VERSION === 'string' ? `${MARKETING_HEADER_AND_FOOTER_VERSION}` : '';
  return `//static.parastorage.com/services/marketing-header-and-footer/${version}/`;
};

const specification = {
  baseUrl: {
    default: baseUrl(),
    parser: value => String(value),
  },
  supportedLanguages: {
    default: supportedLanguages,
    parser: value =>
      arrayWithoutDublicates(
        (typeof value === 'string' ? value.split(' ') : Array.isArray(value) ? value : Object.keys(languages)).filter(
          language => language && language in languages,
        ),
      ),
  },
  languageRedirectUrl: {
    default: null,
    parser: value => (typeof value === 'function' ? value : null),
  },
  language: {
    default: obtainLanguage(fallbackLanguage),
    parser: value => (value in languages ? value : fallbackLanguage),
  },
  forceView: {
    default: null, // can be null, 'desktop' or 'mobile'
    parser: value => (['desktop', 'mobile'].indexOf(value) >= 0 ? value : null),
  },
  screenBreakpoint: {
    default: defaultScreenBreakpoint,
    parser: value => (parseInt(value) >= 0 ? parseInt(value) : defaultScreenBreakpoint),
  },
  postLogin: {
    default: `https://manage.wix.com/account/route`,
    parser: value => String(value),
  },
  postSignUp: {
    default: `https://${domains.wix}/new/intro/`,
    parser: value => String(value),
  },
  postSignOut: {
    default: null,
    parser: value => String(value),
  },
  hostPage: {
    default: null, // can be 'marketing-homepage', 'davinci-homepage', 'templates', 'editor' or null
    parser: value =>
      ['marketing-homepage', 'davinci-homepage', 'templates', 'editor'].indexOf(value) >= 0 ? value : null,
  },
  tabIndex: {
    default: defaultTabIndex,
    parser: value => {
      if (value instanceof Array && value.length === 2 && isInt(value[0]) && isInt(value[1])) {
        return value;
      }
      if (typeof value !== 'string') {
        return defaultTabIndex;
      }
      const array = value.split(' ').filter(string => string);
      return array.length !== 2
        ? defaultTabIndex
        : array.map((string, index) => (isNaN(parseInt(string)) ? defaultTabIndex[index] : parseInt(string)));
    },
  },
  hideMainLinksOnDesktop: {
    default: false,
    parser: value => value === true || typeof value === 'string',
  },
  hideLanguagePickerOnDesktop: {
    default: false,
    parser: value => value === true || typeof value === 'string',
  },
  useOnly: {
    default: null, // can be 'header', 'footer', 'none' or null
    parser: value => (['header', 'footer', 'none'].indexOf(value) >= 0 ? value : null),
  },
  hideHeaderAndFooter: {
    default: null,
    parser: value => value === true || typeof value === 'string',
  },
  prerender: {
    default: null,
    parser: value => value === true || typeof value === 'string',
  },
  accessibilityChanges: {
    default: false,
    parser: value => value === true || typeof value === 'string',
  },
  hideHeader: {
    default: true,
    parser: value => value === true,
  },
  hideFooter: {
    default: true,
    parser: value => value === true,
  },
  isWixHeader2020Sticky: {
    default: null,
    parser: value => value === true,
  },
  languagePickerList: {
    default: null,
    parser: langs => (Array.isArray(langs) ? langs.filter(lang => Object.keys(languages).includes(lang)) : null),
  },
};

export const useMobileVersion = config =>
  config.forceView
    ? config.forceView === 'mobile'
    : (typeof document === 'object' && document.documentElement.clientWidth < config.screenBreakpoint) ||
      (config.accessibilityChanges &&
        typeof document === 'object' &&
        document.documentElement.clientWidth < a11yScreenBreakpoint) ||
      (typeof window !== 'undefined' &&
        window.W &&
        window.W.Site &&
        window.W.Site.Common &&
        window.W.Site.Common.isMobile) ||
      false;

export const getDefault = () => ({ ...mapObject(specification, value => value.default) });

export const sanitize = (input = {}) => {
  const output = mapObject(specification, (value, key) =>
    input[key] === undefined ? clone(value.default) : value.parser(input[key]),
  );
  output.language =
    output.supportedLanguages.concat(notExposedLanguages).indexOf(output.language) < 0 ? 'en' : output.language;
  return output;
};

export const defaultProfileImage = config => `${config.baseUrl}assets/default-profile-image.svg`;

export const obtainFromDOM = (artifact = 'marketing-header-and-footer') => {
  const node = document.querySelector(`script[data-artifact=${artifact}]`);
  if (!node) {
    console.warn('Cannot obtain header and footer config from DOM, fallback to default');
    return getDefault();
  }
  const config = {};
  Object.keys(node.dataset || {})
    .filter(dataAttributeName => dataAttributeName.indexOf('config') === 0)
    .forEach(dataAttributeName => {
      let key = dataAttributeName.substr(6);
      key = key.charAt(0).toLowerCase() + key.slice(1);
      config[key] = node.dataset[dataAttributeName];
      config.baseUrl = node.src.substr(0, node.src.indexOf('app.bundle'));
    });
  return sanitize(config);
};
